import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { getTimezoneShort } from '~/modules-rocco/booking/utils/calendar';
import { isProductSupplierProfessional } from '~/modules/professional/util/is-product-supplier-professional';

dayjs.extend(utc);
dayjs.extend(timezone);

export interface ProfessionalEnquiryContext {
    IsManufacturer?: boolean;
    HasProducts?: boolean;
    ProductsAllowed?: boolean;
}

interface MeetingRequestMessageInput {
    /** Team member the visitor picked, when the professional has more than one. */
    personName?: string | null;
    /** Item the request came from, e.g. a project title. Omitted on a profile page. */
    itemTitle?: string | null;
    /**
     * The professional, used only when no item names the subject. Decides
     * whether the meeting reads as being about their products or about a
     * project — the same split {@link getDefaultEnquiryMessage} makes. Omitted
     * leaves the subject out rather than guessing.
     */
    professional?: ProfessionalEnquiryContext | null;
    /** The visitor's own company, when they gave one. */
    companyName?: string | null;
    /** Chosen start time, epoch ms. */
    startTimestamp: number;
    /** IANA timezone the time should read in, normally the visitor's. */
    timezoneName: string;
    durationMinutes: number;
}

/**
 * What the meeting is about, for the request body.
 *
 * A project or product page names its own subject. A profile has none — it used
 * to pass its own title, so the message told a practice you wanted to meet
 * about themselves — so the subject comes from what they sell instead: products
 * for a supplier, a project for everyone else, mirroring the split
 * {@link getDefaultEnquiryMessage} already makes for enquiries.
 *
 * With neither, the clause is dropped. A request that says only when and with
 * whom is honest; one that invents a subject is not. See BC-5188.
 */
const meetingRequestSubject = ({
    itemTitle,
    professional,
}: Pick<MeetingRequestMessageInput, 'itemTitle' | 'professional'>): string => {
    const title = itemTitle?.trim();
    if (title) {
        return ` about "${title}"`;
    }
    if (!professional) {
        return '';
    }
    return isProductSupplierProfessional({
        isManufacturer: professional.IsManufacturer,
        hasProducts: professional.HasProducts,
        productsAllowed: professional.ProductsAllowed,
    })
        ? ' about your products'
        : ' about a potential project';
};

/**
 * The enquiry body for a meeting request made against a professional with no
 * connected calendar.
 *
 * The time is a preference, not a booking, so the wording asks rather than
 * confirms and invites the professional to propose another time. Built on the
 * server from the posted slot, so the message always matches what was chosen.
 * See BC-5174.
 */
export function getMeetingRequestMessage({
    personName,
    itemTitle,
    professional,
    companyName,
    startTimestamp,
    timezoneName,
    durationMinutes,
}: MeetingRequestMessageInput): string {
    const start = dayjs(startTimestamp).tz(timezoneName);
    const when = start.format('dddd D MMMM, h:mm A');
    // dayjs only understands the `z` token with the advancedFormat plugin
    // loaded, and without it every request went out reading "9:00 AM (z)".
    // This is the same resolver the booking card labels its times with.
    const zoneLabel = getTimezoneShort(timezoneName, startTimestamp);

    const who = personName?.trim() ? ` with ${personName.trim()}` : '';
    const about = meetingRequestSubject({ itemTitle, professional });

    const from = companyName?.trim()
        ? `\n\nI'm with ${companyName.trim()}.`
        : '';

    return (
        `Hi, I'd like to arrange a ${durationMinutes} minute meeting${who}${about}.` +
        `\n\nMy preferred time is ${when} (${zoneLabel}).${from}` +
        `\n\nIf that doesn't suit, please suggest another time that works for you.`
    );
}

/**
 * Generates the default enquiry message based on the item and CTA context.
 */
export function getDefaultEnquiryMessage({
    itemType,
    itemTitle,
    ctaLabel,
    professional,
}: {
    itemType: 'professional' | 'product' | 'project' | 'article';
    itemTitle?: string;
    ctaLabel?: string;
    professional?: ProfessionalEnquiryContext;
}) {
    if (!itemTitle) {
        return '';
    }

    const normalisedCta = ctaLabel?.toLowerCase() ?? '';

    if (normalisedCta.includes('sample')) {
        return (
            `Hi, I'm interested in requesting a sample of ${itemTitle}.` +
            `\n\nCould you please let me know about sample availability` +
            ` and any associated costs?`
        );
    }

    if (normalisedCta.includes('enquire')) {
        return (
            `Hi, I'm interested in ${itemTitle} and would like` +
            ` to discuss this further.` +
            `\n\nCould you please get in touch?`
        );
    }

    if (normalisedCta.includes('question')) {
        return (
            `Hi, I have a question about ${itemTitle}.` +
            `\n\nCould you please get in touch?`
        );
    }

    if (normalisedCta.includes('meeting')) {
        return (
            `Hi, I'd like to request a meeting with ${itemTitle}` +
            ` to discuss a potential project.` +
            `\n\nCould you please let me know your availability?`
        );
    }

    if (normalisedCta.includes('consultation')) {
        return (
            `Hi, I'd like to book a consultation with ${itemTitle}` +
            ` to discuss my project requirements.` +
            `\n\nCould you please let me know your availability?`
        );
    }

    if (normalisedCta.includes('quote')) {
        return (
            `Hi, I came across your project "${itemTitle}"` +
            ` and I'm interested in something similar.` +
            `\n\nCould you please provide a rough estimate` +
            ` and let me know the best way to discuss this further?`
        );
    }

    if (normalisedCta.includes('price') || normalisedCta.includes('brochure')) {
        return (
            `Hi, I'm interested in ${itemTitle} and would like` +
            ` to request pricing information and a brochure.` +
            `\n\nCould you please send these through?`
        );
    }

    switch (itemType) {
        case 'product':
            return (
                `Hi, I'm interested in ${itemTitle} and would like` +
                ` to learn more.` +
                `\n\nCould you please send me pricing` +
                ` and availability information?`
            );
        case 'project':
            return (
                `Hi, I came across your project "${itemTitle}"` +
                ` and would love to learn more.` +
                `\n\nCould you please share further details` +
                ` and indicative pricing?`
            );
        case 'professional':
            if (
                isProductSupplierProfessional({
                    isManufacturer: professional?.IsManufacturer,
                    hasProducts: professional?.HasProducts,
                    productsAllowed: professional?.ProductsAllowed,
                })
            ) {
                return (
                    `Hi, I'm interested in learning more about` +
                    ` ${itemTitle}'s products and would like to discuss` +
                    ` the best options for my project.` +
                    `\n\nCould you please get in touch?`
                );
            }
            return (
                `Hi, I'm interested in learning more about` +
                ` ${itemTitle}'s services and would like to discuss` +
                ` a potential project.` +
                `\n\nCould you please get in touch?`
            );
        default:
            return (
                `Hi, I'd like to learn more about your services.` +
                `\n\nCould you please get in touch?`
            );
    }
}
